import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { csrfFetch } from '~/lib/api/csrf-client';

interface Principal {
  displayName: string;
  email: string;
  organizationName: string;
  role: string;
}

interface AuthState {
  mode: 'legacy' | 'multi-user';
  authenticated: boolean;
  principal: Principal | null;
  registrationAllowed?: boolean;
}

export function AuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState | null>(null);
  const [registering, setRegistering] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordValue, setPasswordValue] = useState('');
  const [error, setError] = useState('');

  const loadSession = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/me', { credentials: 'same-origin' });
      const body = (await response.json()) as { data?: AuthState };
      setState(body.data ?? null);
    } catch {
      setState({ mode: 'multi-user', authenticated: false, principal: null });
      setError('Unable to verify the current session.');
    }
  }, []);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    const form = new FormData(event.currentTarget);
    const payload = registering
      ? {
          displayName: form.get('displayName'),
          organizationName: form.get('organizationName'),
          email: form.get('email'),
          password: form.get('password'),
        }
      : {
          email: form.get('email'),
          password: form.get('password'),
        };

    try {
      const response = await fetch(registering ? '/api/auth/register' : '/api/auth/login', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = (await response.json()) as { message?: string; error?: { message?: string } };

      if (!response.ok) {
        throw new Error(body.error?.message || body.message || 'Authentication failed');
      }

      await loadSession();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Authentication failed');
    } finally {
      setSubmitting(false);
    }
  };

  const logout = async () => {
    await csrfFetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'same-origin',
    });
    setState((current) =>
      current ? { ...current, authenticated: false, principal: null, registrationAllowed: false } : current,
    );
  };

  if (!state) {
    return (
      <div className="grid min-h-screen place-items-center bg-devonz-elements-background-depth-1 text-devonz-elements-textPrimary">
        <div className="i-svg-spinners:90-ring-with-bg h-6 w-6" aria-label="Loading session" />
      </div>
    );
  }

  if (state.mode === 'legacy') {
    return children;
  }

  if (!state.authenticated) {
    const canRegister = state.registrationAllowed === true;

    return (
      <main className="min-h-screen bg-[#f7f8fb] px-4 py-6 text-[#111827] sm:px-6 lg:px-8">
        <div className="mx-auto grid min-h-[calc(100vh-3rem)] max-w-7xl overflow-hidden rounded-[32px] border border-[#e5e7eb] bg-white shadow-[0_24px_80px_rgba(17,24,39,0.10)] lg:grid-cols-[minmax(360px,480px)_1fr]">
          <section className="flex flex-col justify-center px-6 py-10 sm:px-10">
            <div className="mb-10 flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#111827]">
                <img src="/logo.svg" alt="" className="h-7 w-7" />
              </div>
              <div>
                <h1 className="text-xl font-semibold">Excalibur Creator</h1>
                <p className="text-sm text-[#6b7280]">Private AI app builder</p>
              </div>
            </div>

            <form onSubmit={submit} className="flex w-full max-w-sm flex-col gap-4">
              <div>
                <h2 className="text-3xl font-semibold tracking-[-0.02em]">
                  {registering ? 'Create workspace' : 'Welcome back'}
                </h2>
                <p className="mt-2 text-sm leading-6 text-[#6b7280]">
                  {registering ? 'Set up the first organization account.' : 'Sign in to your organization workspace.'}
                </p>
              </div>

              {registering && (
                <>
                  <label className="grid gap-1.5 text-sm font-medium text-[#374151]">
                    Username
                    <input
                      name="displayName"
                      required
                      autoComplete="username"
                      className="h-11 rounded-2xl border border-[#d1d5db] bg-white px-3 text-[#111827] outline-none transition-colors duration-200 focus:border-[#2563eb]"
                    />
                  </label>
                  <label className="grid gap-1.5 text-sm font-medium text-[#374151]">
                    Organization
                    <input
                      name="organizationName"
                      required
                      autoComplete="organization"
                      className="h-11 rounded-2xl border border-[#d1d5db] bg-white px-3 text-[#111827] outline-none transition-colors duration-200 focus:border-[#2563eb]"
                    />
                  </label>
                </>
              )}

              <label className="grid gap-1.5 text-sm font-medium text-[#374151]">
                Email
                <input
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  className="h-11 rounded-2xl border border-[#d1d5db] bg-white px-3 text-[#111827] outline-none transition-colors duration-200 focus:border-[#2563eb]"
                />
              </label>
              <label className="grid gap-1.5 text-sm font-medium text-[#374151]">
                Password
                <span className="relative block">
                  <input
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={6}
                    autoComplete={registering ? 'new-password' : 'current-password'}
                    value={passwordValue}
                    onChange={(event) => setPasswordValue(event.target.value)}
                    className="h-11 w-full rounded-2xl border border-[#d1d5db] bg-white px-3 pr-11 text-[#111827] outline-none transition-colors duration-200 focus:border-[#2563eb]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    className="absolute right-1.5 top-1.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-transparent text-[#6b7280] transition-colors duration-200 hover:bg-[#f3f4f6] hover:text-[#2563eb] focus:outline-none focus:ring-2 focus:ring-[#2563eb]"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <span className={showPassword ? 'i-ph:eye-slash h-4 w-4' : 'i-ph:eye h-4 w-4'} />
                  </button>
                </span>
              </label>

              {registering && (
                <ul className="grid gap-1 text-xs text-[#6b7280]" aria-label="Password requirements">
                  <PasswordRequirement met={passwordValue.length >= 6}>At least 6 characters</PasswordRequirement>
                </ul>
              )}

              {error && (
                <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="mt-2 inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-full bg-[#111827] px-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className={submitting ? 'i-svg-spinners:90-ring-with-bg h-4 w-4' : 'i-ph:arrow-right h-4 w-4'} />
                {submitting ? 'Please wait...' : registering ? 'Create organization' : 'Sign in'}
              </button>

              {canRegister && (
                <button
                  type="button"
                  onClick={() => {
                    setRegistering((value) => !value);
                    setPasswordValue('');
                    setShowPassword(false);
                    setError('');
                  }}
                  className="cursor-pointer border-0 bg-transparent p-0 text-left text-sm font-medium text-[#2563eb] hover:text-[#1d4ed8]"
                >
                  {registering ? 'Back to sign in' : 'Set up the first organization'}
                </button>
              )}
            </form>
          </section>

          <section className="hidden bg-[#f3f6fb] p-10 lg:flex lg:flex-col lg:justify-between">
            <div className="flex justify-end">
              <span className="rounded-full border border-[#dbeafe] bg-white px-3 py-1 text-xs font-semibold text-[#2563eb]">
                Excalibur HQ
              </span>
            </div>
            <div className="mx-auto w-full max-w-xl rounded-[32px] border border-[#e5e7eb] bg-white p-5 shadow-[0_18px_50px_rgba(17,24,39,0.08)]">
              <div className="rounded-[24px] border border-[#e5e7eb] bg-[#f9fafb] p-4">
                <div className="mb-4 flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full bg-[#ef4444]" />
                  <span className="h-3 w-3 rounded-full bg-[#f97316]" />
                  <span className="h-3 w-3 rounded-full bg-[#22c55e]" />
                </div>
                <div className="rounded-2xl bg-white p-4">
                  <p className="text-sm font-semibold text-[#111827]">What do you want to build?</p>
                  <p className="mt-3 rounded-2xl bg-[#eff6ff] p-4 text-sm leading-6 text-[#374151]">
                    Create a client portal with approvals, files and delivery tracking.
                  </p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <div className="rounded-2xl border border-[#e5e7eb] bg-white p-3">
                      <span className="i-ph:database h-5 w-5 text-[#2563eb]" />
                      <p className="mt-2 text-xs font-medium text-[#111827]">Data model</p>
                    </div>
                    <div className="rounded-2xl border border-[#e5e7eb] bg-white p-3">
                      <span className="i-ph:layout h-5 w-5 text-[#f97316]" />
                      <p className="mt-2 text-xs font-medium text-[#111827]">Interface</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <p className="max-w-xl text-4xl font-semibold leading-tight tracking-[-0.02em] text-[#111827]">
              Private apps for operations, clients and internal automations.
            </p>
          </section>
        </div>
      </main>
    );
  }

  return (
    <>
      {children}
      <div className="fixed bottom-3 right-3 z-[100] flex max-w-[calc(100vw-1.5rem)] items-center gap-2 rounded-full border border-[#e5e7eb] bg-white/95 px-2 py-1 text-xs text-[#6b7280] shadow-[0_12px_40px_rgba(17,24,39,0.10)] backdrop-blur">
        <div className="min-w-0 max-w-[11rem] sm:max-w-[16rem]">
          <p className="truncate leading-4 font-semibold text-[#111827]">{state.principal?.organizationName}</p>
          <p className="truncate leading-4 text-[#6b7280]">{state.principal?.displayName}</p>
        </div>
        <button
          type="button"
          onClick={logout}
          className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-[#e5e7eb] bg-white text-[#6b7280] transition-colors duration-200 hover:border-[#2563eb] hover:text-[#2563eb] focus:outline-none focus:ring-2 focus:ring-[#2563eb]"
          title="Sign out"
          aria-label="Sign out"
        >
          <span className="i-ph:sign-out h-4 w-4" />
        </button>
      </div>
    </>
  );
}

function PasswordRequirement({ met, children }: { met: boolean; children: ReactNode }) {
  return (
    <li className={met ? 'flex items-center gap-2 text-[#2563eb]' : 'flex items-center gap-2 text-[#6b7280]'}>
      <span className={met ? 'i-ph:check-circle h-3.5 w-3.5' : 'i-ph:circle h-3.5 w-3.5'} />
      <span>{children}</span>
    </li>
  );
}
